import {html, LitElement} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {repeat} from 'lit/directives/repeat.js';
import {PubSub} from '@mamdev/cad.static-next-lib';

import {AjaxPost} from '../../ts/services/ajax-post';
import Tracking2 from '../../ts/services/tracking2';
import styles from './feedback.styles';

@customElement('ui-feedback')
export class FeedBack extends LitElement {
  static readonly styles = styles;

  @state() isresponseError = false;
  @state() isSuccessful = false;
  @state() currentInputLength = 0;
  @state() placeholderText = 'Schreiben Sie uns.';

  @property() headline = '';
  @property() contentId = '';
  @property() privacyUrl = '';
  @property() actionUrl = '';
  @property() informationUrl = '#';

  private readonly categories = [
    {id: '0', category: 'Lob', text: 'Lassen Sie uns gerne wissen, was Ihnen an diesem Beitrag gefällt.', clickname: 'feedbackmail_lob'},
    {id: '1', category: 'Kritik', text: 'Lassen Sie uns gerne wissen, was Sie an diesem Beitrag oder unserer Berichterstattung stört.', clickname: 'feedbackmail_kritik'},
    {id: '2', category: 'Fehlerhinweis', text: 'Wir sind dankbar, wenn Sie uns auf Fehler hinweisen.', clickname: 'feedbackmail_fehlerhinweis'},
    {id: '3', category: 'Meinung zum Thema', text: 'Wir freuen uns, wenn Sie Ihre Meinung zum Thema des Beitrags mit uns teilen.', clickname: 'feedbackmail_meinung'},
    {id: '4', category: 'Fragen', text: 'Sie haben Fragen an die Redaktion? Treten Sie mit uns in Kontakt.', clickname: 'feedbackmail_fragen'}
  ];

  get componentPath() {
    return this.getAttribute('data-component-path') ?? '';
  }

  inputRadioHandler(event: Event) {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const category = this.categories.find(e => e.id === input.id);

    input.parentElement?.classList.remove('error');
    if (category) {
      this.placeholderText = category.text;
      PubSub.publish('feedback:form', {
        referrer: '&referrer=undefined',
        click1: '&click1=undefined',
        click2: '&click2=undefined',
        click3: '&click3=undefined',
        click4: '&click4=undefined',
        clickname: '&clickname=' + category.clickname
      });
      Tracking2.callTealiumLink({
        businessEventType: 'userAction',
        componentPath: `${this.componentPath}.form-group.btn-group.category.${input.id}`,
        componentLabel: category.category,
        elementTagName: 'input',
        eventType: 'mousedown'
      });
    }
  }

  validationHandler(event: Event) {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    if (input.name === 'category') {
      input.parentElement?.classList.add('error');
    } else {
      input.classList.add('error');
    }
  }

  inputHandler(event: Event) {
    const input = event.target as HTMLInputElement;

    if (input.validity.valid) {
      input.classList.remove('error');
    }

    if (input.name === 'message') {
      this.currentInputLength = input.value.length;
    }
  }

  sendFeedbackHandler() {
    return {
      handleEvent: (e: Event) => {
        e.preventDefault();
        const form = e.target;

        // collect and send feedback
        const formData = new FormData(form as HTMLFormElement);
        const feedbackObj: Record<string, FormDataEntryValue> = {};
        for (const [key, value] of formData.entries()) {
          feedbackObj[key] = value;
        }
        this.onSendFeedback(feedbackObj);
      }
    };
  }

  onSendFeedback(feedback: Record<string, FormDataEntryValue>) {
    // missing error screen
    if (this.actionUrl) {
      AjaxPost.postJSON(this.actionUrl, feedback)
        .then(() => {
          console.log('Sent successfully');
          PubSub.publish('track:dialogShow', {
            referrer: '&referrer=undefined',
            click1: '&click1=' + window.ui.pageinfo.coremid.toString(),
            click2: '&click2=feedbackmail',
            click3: '&click3=success',
            click4: '&click4=1',
            clickname: '&clickname=feedbackmail_success'
          });
          Tracking2.callTealiumLink({
            businessEventType: 'userAction',
            componentPath: `${this.componentPath}.form-group.btn-group.submit`,
            componentLabel: 'Senden',
            elementTagName: 'button',
            eventType: 'mousedown'
          });
          PubSub.publish('content:successfulFeedbackDialog');
          this.isSuccessful = true;
          // reset word count after sending feedback
          this.currentInputLength = 0;
          this.isresponseError = false;
        })
        .catch(() => {
          console.log(
            'Server error when posting Feedback. Showing general error message.'
          );
          //  show error screen instead
          this.isresponseError = true;
        });
    }
  }

  back() {
    PubSub.publish('content:closeFeedbackDialog');
  }

  moreInformation(e: Event) {
    const target = e.target as HTMLElement;
    const componentLabel = target.innerHTML ?? target.getAttribute('aria-label') ?? '';

    PubSub.publish('track:dialogShow', {
      referrer: '&referrer=undefined',
      click1: '&click1=' + window.ui.pageinfo.coremid.toString(),
      click2: '&click2=feedbackmail',
      click3: '&click3=success_informationen',
      click4: '&click4=1',
      clickname: '&clickname=feedbackmail_success_informationen'
    });

    Tracking2.callTealiumLink({
      businessEventType: 'userAction',
      componentPath: this.componentPath + '.success_informationen',
      componentLabel,
      elementTagName: 'a',
      eventType: 'mousedown'
    });
  }

  render() {
    return html`
      <div class="dialog__wrapper">
        <div class="dialog__title">Kontakt in die Redaktion</div>
        ${this.isSuccessful ? html`
          <div class="dialog__success">
            <div class="logo"></div>
            <h3 class="heading">Vielen Dank für Ihr Feedback!</h3>
            <p class="sub-heading">
              Anregungen und Hinweise unserer Leserinnen und Leser sind sehr
              wertvoll für uns und fließen auf vielen Wegen in unsere
              redaktionelle Arbeit ein. <br />
              <a href="${this.informationUrl}" @click=${this.moreInformation} target="_blank">Mehr Informationen</a>.
            </p>
            <button @click=${this.back} class="btn secondary">
              Zurück zum Beitrag
            </button>
          </div>` : html`
            <div class="dialog__form">
              <form @submit=${this.sendFeedbackHandler()}>
                <label class="form-label">Feedback zum Beitrag</label>
                <div class="dialog__description">${this.headline}</div>
                <div class="form-group">
                  <label class="form-label">Bitte wählen Sie die Art des Feedbacks*</label>
                  <div class="btn-group" role="group">
                    ${repeat(this.categories, item => item.id, (item, index) => html`
                      <input
                        type="radio"
                        id="${item.id}"
                        name="category"
                        value="${item.category}"
                        tabindex="${index + 1}"
                        required
                        @change=${this.inputRadioHandler}
                        @invalid=${this.validationHandler}
                      /><label class="btn secondary" for="${item.id}">${item.category}</label>
                    `)}
                  </div>
                  <div class="dialog__error">
                    Bitte wählen Sie die Art des Feedbacks aus.
                  </div>
                </div>
                <input
                  type="hidden"
                  name="contentId"
                  value="${this.contentId}"
                />
                <div class="form-group">
                  <label class="form-label" for="message">Ihr Feedback*</label>
                  <textarea
                    tabindex="6"
                    name="message"
                    placeholder="${this.placeholderText}"
                    required
                    maxlength="1000"
                    @invalid=${this.validationHandler}
                    @input=${this.inputHandler}
                    @change=${this.inputHandler}
                    class="form-control dialog__usercontent dialog__message"
                  ></textarea>
                  <div class="dialog__error">
                    Bitte geben Sie einen Text ein.
                  </div>
                  <div class="word-count ${this.currentInputLength > 999 ? 'error' : null}">
                    ${this.currentInputLength}/1000
                  </div>
                </div>

                <div class="form-group">
                  <label class="form-label" for="fromEmail">Ihre E-Mail-Adresse*</label>
                  <input
                    type="email"
                    tabindex="7"
                    name="fromEmail"
                    placeholder="E-Mail-Adresse"
                    required
                    @invalid=${this.validationHandler}
                    @input=${this.inputHandler}
                    class="form-control dialog__useremail"
                  />
                  <div class="dialog__error">
                    Bitte geben Sie eine gültige E-Mail-Adresse ein.
                  </div>
                </div>

                <div class="form-group">
                  <input
                    type="checkbox"
                    tabindex="8"
                    id="acceptGDPR"
                    name="acceptGDPR"
                    required
                    value="true"
                    @invalid=${this.validationHandler}
                    @input=${this.inputHandler}
                    class="accept form-control custom-checkbox"
                  />

                  <label for="acceptGDPR" class="form-label dialog__hint">
                    <div>
                      Ich stimme zu, dass meine E-Mail-Adresse zur Bearbeitung gespeichert wird. Um Fehlerhinweise schnellstmöglich zu bearbeiten, werden Informationen zu Browser und Betriebssystem ("User-Agent") gespeichert.
                      <br />Weitere Informationen finden Sie in unseren
                      <a href="${this.privacyUrl}" target="_blank">Datenschutzhinweisen</a>*
                    </div>
                  </label>
                  <div class="dialog__error">
                    Bitte stimmen Sie den Datenschutzhinweisen zu.
                  </div>
                </div>

                <div class="form-group">
                  <input
                    type="checkbox"
                    tabindex="9"
                    id="publishConsent"
                    name="publishConsent"
                    value="true"
                    class="accept form-control custom-checkbox"
                  />

                  <label for="publishConsent" class="form-label dialog__hint">
                    <div>
                      Ich stimme zu, dass die Redaktion mein Feedback – gegebenenfalls sinnwahrend gekürzt und bearbeitet – veröffentlichen darf. Dabei dürfen mein Vorname, mein Alter und mein Wohnort (falls von mir angegeben) genannt werden. Meine E-Mail-Adresse wird nicht veröffentlicht.
                    </div>
                  </label>
                </div>

                <div class="required">* Pflichtfelder</div>
                <div class="form-group right">
                  <button type="submit" class="btn primary" tabindex="10">
                  Senden <!-- Replace the componentLabel for tracking to same button text anytime you change button text -->
                  </button>
                </div>
                ${this.isresponseError ? html`
                  <div class="responseError">
                    <span class="icon-warning"></span>
                    <div class="error-hint">
                      Leider gibt es derzeit ein technisches Problem. Bitte
                      versuchen Sie es in wenigen Minuten wieder.
                    </div>
                  </div>
                ` : null}
              </form>
            </div>
        `}
      </div>
    `;
  }
}
